"use client";

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from "react";
import type { Dictionary } from "@/dictionaries/types";
import { SecGlyph } from "@/features/home-v2/ornament";
import { StatePanel } from "@/features/home-v2/ui";
import { HOLDERS_LIMIT, useTopHolders } from "../data/useDetailData";
import FitHex from "../FitHex";
import { RetryButton, RowsSkeleton } from "../parts";
import { withPercent } from "@/lib/percent";
type Props = {
  contract: string;
  lang: "fa" | "en";
  t: Dictionary["collectionPage"];
  states: Dictionary["states"];
  emptyAction: ReactNode;
};

/** The share bar, filling from the reading edge; never thinner than a mark. */
function Bar(t0) {
  const $ = _c(2);
  const {
    share
  } = t0;
  const t1 = `${Math.min(100, Math.max(share, share > 0 ? 3 : 0))}%`;
  let t2;
  if ($[0] !== t1) {
    t2 = <span className="vt-bar" aria-hidden={true}><i style={{
        inlineSize: t1
      }} /></span>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  return t2;
}

/**
 * THE TOP HOLDERS AS A PODIUM — `useTopHolders`, the legacy query and its
 * share-of-shown, unchanged, and the scope said in words. The three largest
 * stand on the podium the /collection list uses for its top three (outlined
 * gold numerals); the rest follow as a ranked list.
 */
export default function VitrineHolders(t0) {
  const $ = _c(12);
  const {
    contract,
    lang,
    t,
    states,
    emptyAction
  } = t0;
  const locale = lang === "fa" ? "fa-IR" : "en-US";
  const nf = new Intl.NumberFormat(locale);
  let t1;
  if ($[0] !== lang || $[1] !== locale) {
    t1 = n => withPercent(n.toLocaleString(locale, {
      maximumFractionDigits: 1
    }), lang === "fa");
    $[0] = lang;
    $[1] = locale;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const pct = t1;
  const {
    holders,
    isLoading,
    isSuccess,
    retry
  } = useTopHolders(contract, lang);
  if (isLoading && holders.length === 0) {
    let t2;
    if ($[3] !== states.loading) {
      t2 = <RowsSkeleton label={states.loading} rows={HOLDERS_LIMIT} />;
      $[3] = states.loading;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  if (holders.length === 0) {
    let t2;
    if ($[5] !== emptyAction || $[6] !== isSuccess || $[7] !== retry || $[8] !== states || $[9] !== t) {
      t2 = isSuccess ? <StatePanel title={t.holdersEmptyTitle} body={t.holdersEmptyBody} action={emptyAction} /> : <StatePanel alert={true} title={states.errorTitle} body={states.errorBody} action={<RetryButton label={states.retry} onRetry={retry} />} />;
      $[5] = emptyAction;
      $[6] = isSuccess;
      $[7] = retry;
      $[8] = states;
      $[9] = t;
      $[10] = t2;
    } else {
      t2 = $[10];
    }
    return t2;
  }
  const scope = t.holdersScope.replace(/\{count\}/g, nf.format(holders.length));
  const podium = holders.slice(0, 3);
  const rest = holders.slice(3);
  let t2;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <SecGlyph className="vt-glyph" />;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return <div className="vt-stack" aria-label={`${t.holdersTableLabel} — ${scope}`} role="region"><p className="vt-scope vt-scope--mark">{t2}{scope}</p><ol className="vt-podium">{podium.map((h, i) => <li key={`${h.address}-${i}`} className="vt-podium-card" data-rank={i + 1}><span className="vt-podium-no" aria-hidden={true}>{nf.format(i + 1)}</span><span className="v2-sr-only">{`${t.columnRank} ${nf.format(i + 1)}`}</span><span className="vt-podium-who">{h.name ? <b>{h.name}</b> : null}<FitHex value={h.address} /></span><span className="vt-podium-owned"><b>{nf.format(h.owned)}</b><small>{t.columnOwned}</small></span><span className="vt-podium-share"><Bar share={h.share} /><span>{pct(h.share)}</span></span></li>)}</ol>{rest.length > 0 ? <ol className="vt-ranks" start={4}>{rest.map((h_0, i_0) => <li key={`${h_0.address}-${i_0}`} className="vt-rank"><span className="vt-rank-no">{nf.format(i_0 + 4)}</span><span className="vt-rank-who">{h_0.name ? <b>{h_0.name}</b> : null}<FitHex value={h_0.address} /></span><span className="vt-rank-owned"><b>{nf.format(h_0.owned)}</b></span><span className="vt-rank-share"><Bar share={h_0.share} /><span>{pct(h_0.share)}</span></span></li>)}</ol> : null}</div>;
}